import React, { useRef, useState } from 'react';
import Modal from 'react-responsive-modal';
import { Button } from '@synergy-marine-group/synergy-ui';
import uploadFixedBudgetVessel from './queries/uploadFixedBudgetVessel';
import { validateFixedBudgetVesselCsv } from './validateFixedBudgetVesselCsv';
import showAlert from '../../utils/alert';
import { deleteModalStyles } from '../Modal/styles';

const FIXED_BUDGET_TOAST_CLASS = 'fixed-budget-toast-multiline';
const FIXED_BUDGET_TOAST_BODY_CLASS = 'fixed-budget-toast-multiline-body';

const getFixedBudgetToastOptions = (message) => {
  const text = String(message || '');
  if (!text.includes('\n') && !text.includes('\r')) {
    return undefined;
  }
  return {
    className: FIXED_BUDGET_TOAST_CLASS,
    bodyClassName: FIXED_BUDGET_TOAST_BODY_CLASS,
    containerId: 'fixed-budget-toast',
    autoClose: 10000
  };
};

const normalizeBackendErrors = (errors = []) => {
  const list = [];
  (Array.isArray(errors) ? errors : []).forEach((err) => {
    const text = typeof err === 'string' ? err : err?.message;
    if (text && String(text).trim()) list.push(String(text).trim());
  });
  return Array.from(new Set(list));
};

const getBackendUploadMessage = (data) => (
  typeof data?.message === 'string'
    ? data.message.trim().replace(/\r\n/g, '\n')
    : ''
);

const UploadFixedBudgetVesselModal = ({ open, setOpen }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [clientErrors, setClientErrors] = useState([]);
  const {
    mutate: uploadFile,
    isLoading: isUploading,
    reset: resetUpload
  } = uploadFixedBudgetVessel();

  const resetState = () => {
    setSelectedFile(null);
    setClientErrors([]);
    resetUpload();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    setOpen(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setClientErrors([]);
    if (!file) {
      setSelectedFile(null);
      return;
    }
    const name = file.name?.toLowerCase() || '';
    if (!name.endsWith('.csv')) {
      setSelectedFile(null);
      setClientErrors(['Please select a valid CSV file.']);
      return;
    }
    setSelectedFile(file);
  };

  const parseResponseData = (raw) => {
    if (raw == null) return {};
    if (typeof raw === 'object' && !(raw instanceof Blob)) return raw;
    if (typeof raw === 'string') {
      const text = raw.trim();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (e) {
        return { message: text };
      }
    }
    return {};
  };

  const handleUploadResult = (response) => {
    const data = parseResponseData(response?.data);
    const status = Number(response?.status);
    const backendErrors = normalizeBackendErrors(data?.errors || data?.errorMessages || []);
    const backendMessage = getBackendUploadMessage(data);
    const isSuccess = data?.isSuccess === true
      && backendErrors.length === 0
      && status >= 200
      && status < 300;

    if (backendErrors.length > 0) {
      setClientErrors(backendErrors);
      backendErrors.forEach((err) => showAlert(err, 'error'));
    }

    if (backendMessage) {
      const alreadyShown = backendErrors.some(
        (err) => err === backendMessage || backendMessage.includes(err)
      );
      if (!alreadyShown) {
        showAlert(
          backendMessage,
          isSuccess ? 'success' : 'error',
          getFixedBudgetToastOptions(backendMessage)
        );
      }
    }

    if (!backendMessage && backendErrors.length === 0 && !isSuccess && status >= 400) {
      showAlert('Upload failed. Please fix the file and upload again from the beginning.', 'error');
    }

    if (isSuccess) {
      handleClose();
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setClientErrors(['Please select a CSV file to upload.']);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const { errors, csvBlob } = validateFixedBudgetVesselCsv(reader.result);
      if (errors.length > 0) {
        setClientErrors(errors);
        errors.forEach((err) => showAlert(err, 'error'));
        return;
      }

      const trimmedFile = new File(
        [csvBlob],
        selectedFile.name || 'Fixed_Budget_Vessel_Template.csv',
        { type: 'text/csv' }
      );

      uploadFile(trimmedFile, {
        onSuccess: (response) => handleUploadResult(response),
        onError: (error) => {
          if (!error?.response) {
            showAlert('Something went wrong while uploading fixed budget vessels', 'error');
            return;
          }
          const data = parseResponseData(error.response.data);
          if (
            data
            && (data.isSuccess != null
              || Array.isArray(data.errors)
              || data.message)
          ) {
            handleUploadResult({ data, status: error.response.status });
            return;
          }
          if (typeof error.response.data === 'string' && error.response.data.trim()) {
            setClientErrors([error.response.data]);
            showAlert(error.response.data, 'error');
            return;
          }
          showAlert('Something went wrong while uploading fixed budget vessels', 'error');
        }
      });
    };
    reader.onerror = () => {
      showAlert('Failed to read the selected file', 'error');
    };
    reader.readAsText(selectedFile);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeOnOverlayClick={false}
      center
      showCloseIcon={false}
      styles={deleteModalStyles}
    >
      <div className="w-auto py-8 px-8 font-muli" style={{ minWidth: '28rem' }}>
        <h2 className="uppercase text-center pb-6 font-montserrat font-bold">
          Upload Fixed Budget Vessels
        </h2>

        <div className="text-left pb-2 text-sm">
          CSV columns: SapCompanyCode, FixedSapVesselCompanyCode, FixedCostCenter
        </div>
        <div className="text-left pb-4 text-xs text-gray-500">
          Any error in the file fails the entire upload.
          {' '}
          Re-upload the full file after fixing issues.
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          className="block w-full text-sm mb-4"
        />

        {clientErrors.length > 0 && (
          <div className="text-left text-sm text-red-500 pb-4 max-h-40 overflow-auto">
            {clientErrors.map((err) => (
              <div key={err}>{err}</div>
            ))}
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button
            className="max-w-none font-montserrat text-gray-500 uppercase text-sm"
            onClick={handleClose}
            size="large"
            variant="outlined"
            rounded
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            className="max-w-none font-montserrat text-gray-500 uppercase text-sm"
            onClick={handleUpload}
            size="large"
            variant=" filled"
            rounded
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadFixedBudgetVesselModal;
